import type { ProviderLicenseType } from '@store/generated/organizations';

export interface ProviderLicenseTypeOption {
  value: ProviderLicenseType;
  label: string;
}

/** Canonical provider-license menu shared with the existing member billing form. */
export const PROVIDER_LICENSE_TYPE_OPTIONS: ProviderLicenseTypeOption[] = [
  { value: 'MD', label: 'MD - Doctor of Medicine' },
  { value: 'DO', label: 'DO - Doctor of Osteopathic Medicine' },
  { value: 'PhD', label: 'PhD - Doctor of Philosophy (Psychology)' },
  { value: 'PsyD', label: 'PsyD - Doctor of Psychology' },
  { value: 'LCSW', label: 'LCSW - Licensed Clinical Social Worker' },
  { value: 'LMSW', label: 'LMSW - Licensed Master Social Worker' },
  { value: 'LISW', label: 'LISW - Licensed Independent Social Worker' },
  { value: 'LICSW', label: 'LICSW - Licensed Independent Clinical Social Worker' },
  { value: 'LCSW-C', label: 'LCSW-C - Licensed Certified Social Worker - Clinical' },
  { value: 'LMFT', label: 'LMFT - Licensed Marriage and Family Therapist' },
  { value: 'LPC', label: 'LPC - Licensed Professional Counselor' },
  { value: 'LCPC', label: 'LCPC - Licensed Clinical Professional Counselor' },
  { value: 'LPCC', label: 'LPCC - Licensed Professional Clinical Counselor' },
  { value: 'LMHC', label: 'LMHC - Licensed Mental Health Counselor' },
  { value: 'LCMHC', label: 'LCMHC - Licensed Clinical Mental Health Counselor' },
  { value: 'NP', label: 'NP - Nurse Practitioner' },
  { value: 'PMHNP', label: 'PMHNP - Psychiatric Mental Health Nurse Practitioner' },
  { value: 'APRN', label: 'APRN - Advanced Practice Registered Nurse' },
  { value: 'CNS', label: 'CNS - Clinical Nurse Specialist' },
  { value: 'PA', label: 'PA - Physician Assistant' },
  { value: 'RN', label: 'RN - Registered Nurse' },
  { value: 'LADC', label: 'LADC - Licensed Alcohol and Drug Counselor' },
  { value: 'LCADC', label: 'LCADC - Licensed Clinical Alcohol and Drug Counselor' },
  { value: 'CADC', label: 'CADC - Certified Alcohol and Drug Counselor' },
  { value: 'BCBA', label: 'BCBA - Board Certified Behavior Analyst' },
  { value: 'PSYCH_ASSOC', label: 'Psychology Associate' },
  { value: 'PASTORAL', label: 'Pastoral Counselor' },
  { value: 'OTHER', label: 'Other' },
];

const PROVIDER_LICENSE_TYPE_VALUES = new Set<string>(
  PROVIDER_LICENSE_TYPE_OPTIONS.map(option => option.value),
);

const LEGACY_LICENSE_TYPE_ALIASES: Partial<Record<string, ProviderLicenseType>> = {
  'PMHNP-BC': 'PMHNP',
};

function isProviderLicenseType(value: string): value is ProviderLicenseType {
  return PROVIDER_LICENSE_TYPE_VALUES.has(value);
}

export function normalizeProviderLicenseTypes(values: string[]): ProviderLicenseType[] | null {
  const normalized: ProviderLicenseType[] = [];
  for (const value of values) {
    const candidate = LEGACY_LICENSE_TYPE_ALIASES[value] ?? value;
    if (!isProviderLicenseType(candidate)) return null;
    if (!normalized.includes(candidate)) {
      normalized.push(candidate);
    }
  }
  return normalized;
}
